import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Sparkles, Star, BookOpen, Heart, Quote } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ContactFormModal } from '../components/ContactFormModal';
import { useSiteContent } from '../contexts/SiteContentContext';
import { SEOHead, PersonStructuredData, WebsiteStructuredData } from '../components/SEOHead';
import AFFIRMATIONS from '../data/affirmations';

const getDailyAffirmation = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return AFFIRMATIONS[dayOfYear % AFFIRMATIONS.length];
};

export const HomePage = () => {
  const pageRef = useScrollReveal();
  const { get, testimonials } = useSiteContent();
  const [modal, setModal] = useState({ open: false, type: 'booking_call' });

  const openForm = (type) => setModal({ open: true, type });
  const closeForm = () => setModal({ open: false, type: 'booking_call' });

  const hero = get('homepage_hero');
  const about = get('about_natasha');
  const book = get('book_section');
  const homeTestimonials = testimonials('home');
  const bookLink = book.stripe_link;
  const dailyAffirmation = useMemo(getDailyAffirmation, []);

  return (
    <main className="" ref={pageRef}>
      <SEOHead path="/" />
      <PersonStructuredData />
      <WebsiteStructuredData />
      <ContactFormModal isOpen={modal.open} onClose={closeForm} inquiryType={modal.type} sourcePage="home" />

      {/* Hero — Full-width editorial */}
      <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-end overflow-hidden" data-testid="hero-section">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="/images/natasha-salon-hero.jpg"
            alt="Natasha Simone mentoring beauty professionals in a salon"
            className="w-full h-full object-cover object-[center_20%]"
          />
          {/* Soft gradient overlay — warm, not dark */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(30,15,20,0.75) 0%, rgba(30,15,20,0.4) 35%, rgba(30,15,20,0.05) 65%, transparent 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(30,15,20,0.3) 0%, transparent 60%)' }} />
        </div>

        {/* Content */}
        <div className="relative container-padding pb-16 md:pb-24 pt-40">
          <div className="max-w-2xl">
            <span className="reveal inline-block px-4 py-1.5 bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium tracking-[0.2em] uppercase rounded-full border border-white/20 mb-6">
              {hero.badge_text}
            </span>
            <h1 className="reveal reveal-delay-1 text-4xl md:text-5xl lg:text-6xl xl:text-[4.25rem] leading-[1.08] tracking-tight font-medium text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              {hero.heading_line1}<br />
              <span style={{ color: '#F4B8D0' }}>{hero.heading_line2}</span>
            </h1>
            <p className="reveal reveal-delay-2 text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-lg">
              {hero.description}
            </p>
            <div className="reveal reveal-delay-3 flex flex-wrap gap-3">
              <button onClick={() => openForm('booking_call')} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#1A1A1A] text-xs font-medium tracking-[0.15em] uppercase rounded-full transition-all duration-350 hover:-translate-y-0.5 hover:shadow-xl arrow-hover" data-testid="hero-book-call">
                Book a Free Call <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/business" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white/40 text-white text-xs font-medium tracking-[0.15em] uppercase rounded-full transition-all duration-350 hover:bg-white/10 hover:border-white/70">
                Explore Programs
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Quote — Natasha Simone */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden" data-testid="brand-message-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#E84993]/4 rounded-full blur-[100px]" />
        <div className="container-padding relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="reveal text-[#E84993]/15 text-[8rem] md:text-[10rem] leading-none font-serif select-none" aria-hidden="true" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>"</div>
            <blockquote className="reveal reveal-delay-1 -mt-16 md:-mt-20">
              <p className="text-xl md:text-2xl lg:text-3xl font-medium text-[#1A1A1A] leading-relaxed md:leading-relaxed" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Natasha Simone is all about growing the <span className="text-[#E84993]">inner beauty</span> of women all around the world. We groom, train, build and love places where inner beauty can grow. Why? Because it's all that matters.
              </p>
            </blockquote>
            <div className="reveal reveal-delay-2 w-16 h-0.5 bg-[#E84993] mx-auto mt-8 mb-5" />
            <p className="reveal reveal-delay-2 text-sm tracking-[0.15em] uppercase text-[#E84993] font-medium">— Natasha Simone</p>
          </div>
        </div>
      </section>

      {/* About Natasha */}
      <section className="py-14 md:py-18 bg-white" data-testid="about-natasha-section">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto text-center">
            <span className="reveal overline-text mb-4 block">{about.overline}</span>
            <h2 className="reveal reveal-delay-1 heading-lg mb-6">{about.heading}</h2>
            <div className="reveal reveal-delay-1 divider-gradient w-32 mx-auto mb-6" />
            <p className="reveal reveal-delay-2 body-text max-w-2xl mx-auto">{about.description}</p>
          </div>
        </div>
      </section>

      {/* Gateway Split */}
      <section className="section-padding bg-cream" data-testid="gateway-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="reveal overline-text mb-4 block">Choose Your Path</span>
            <h2 className="reveal reveal-delay-1 heading-lg">Two Transformative Journeys</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Link to="/business" className="reveal group relative overflow-hidden rounded-3xl p-8 md:p-10 hover-lift bg-white border border-[#F0E6F6]" data-testid="gateway-business-link">
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#E84993]/4 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E84993, #D13A7A)' }}><TrendingUp className="w-6 h-6 text-white" /></div>
                  <span className="text-xs uppercase tracking-[0.15em] text-[#E84993]">For Salon Owners & Teams</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-medium text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Diverse Intelligence<br />Systems</h3>
                <p className="text-[#6A6A6A] mb-8 max-w-sm">Scale your salon with proven systems, strategic coaching, and team development frameworks.</p>
                <span className="inline-flex items-center gap-2 text-[#E84993] text-sm font-medium group-hover:gap-4 transition-all">Explore Business Programs <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
            <Link to="/personal" className="reveal reveal-delay-2 group relative overflow-hidden rounded-3xl p-8 md:p-10 hover-lift bg-white border border-[#F0E6F6]" data-testid="gateway-personal-link">
              <div className="absolute top-0 right-0 w-40 h-40 bg-[#B565CC]/4 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B565CC, #9B4FBF)' }}><Sparkles className="w-6 h-6 text-white" /></div>
                  <span className="text-xs uppercase tracking-[0.15em] text-[#B565CC]">For Personal Growth</span>
                </div>
                <h3 className="heading-md text-[#1A1A1A] mb-4">The Beauty<br />of You</h3>
                <p className="text-[#6A6A6A] mb-8 max-w-sm">Build confidence, clarity, and leadership through mindset coaching and personal development.</p>
                <span className="inline-flex items-center gap-2 text-[#B565CC] text-sm font-medium group-hover:gap-4 transition-all">Explore Personal Development <ArrowRight className="w-4 h-4" /></span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Daily Affirmation — glowing pink/lavender */}
      <section className="py-16 md:py-20 bg-affirmation relative overflow-hidden" data-testid="daily-affirmation-section">
        <div className="absolute inset-0 dot-pattern" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#E84993]/6 rounded-full blur-3xl shimmer-glow" />
        <div className="container-padding relative">
          <div className="max-w-2xl mx-auto text-center">
            <span className="reveal badge mb-5 inline-flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Daily Affirmation
            </span>
            <div className="reveal reveal-delay-1 affirmation-enter">
              <p className="text-2xl md:text-3xl lg:text-4xl font-medium leading-snug text-[#3A1F3D] mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }} data-testid="affirmation-text">
                "{dailyAffirmation}"
              </p>
            </div>
            <div className="reveal reveal-delay-2 w-12 h-0.5 bg-[#E84993] mx-auto mb-5" />
            <p className="reveal reveal-delay-2 text-sm text-[#9B4FBF] italic mb-8">A reminder for your inner beauty today.</p>
            <button onClick={() => openForm('personal_dev')} className="reveal reveal-delay-3 btn-primary arrow-hover" data-testid="affirmation-cta">
              Start Your Journey <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-20 bg-white" data-testid="testimonials-section">
        <div className="container-padding">
          <div className="text-center mb-12">
            <span className="reveal overline-text mb-3 block">What Clients Say</span>
            <h2 className="reveal reveal-delay-1 heading-lg">Trusted by Beauty Professionals</h2>
            <div className="reveal reveal-delay-1 divider-gradient w-24 mx-auto mt-5" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {homeTestimonials.map((t, i) => (
              <div key={i} className={`reveal ${i > 0 ? `reveal-delay-${i + 1}` : ''} testimonial-warm`}>
                <div className="flex gap-1 mb-4">{[...Array(t.rating || 5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[#E84993] text-[#E84993]" />)}</div>
                <Quote className="w-6 h-6 text-[#E84993]/15 mb-3" />
                <p className="text-[#4A4A4A] mb-6 leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium" style={{ background: 'linear-gradient(135deg, #E84993, #B565CC)' }}>
                    {t.name.charAt(0)}
                  </div>
                  <div><p className="font-medium text-[#1A1A1A] text-sm">{t.name}</p><p className="text-xs text-[#7A7A7A]">{t.role}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Section */}
      <section className="section-padding bg-cream" data-testid="homepage-book-section">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5 flex justify-center">
              <div className="reveal-image relative">
                <div className="absolute -bottom-5 -right-5 w-full h-full bg-[#E84993]/8 rounded-2xl" />
                <div className="absolute -top-5 -left-5 w-full h-full bg-[#B565CC]/8 rounded-2xl" />
                <div className="relative w-56 md:w-64 aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl shadow-[#E84993]/15">
                  <img src="/images/SalonTeam/Website/PERSONAL_BOY/Book/Natasha-Simone_-Images-1-e1764782647476.png" alt="The Beauty of You — by Natasha Simone" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <span className="reveal badge mb-5 inline-flex items-center gap-2">
                <BookOpen className="w-3.5 h-3.5" /> Featured Book
              </span>
              <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.15] font-medium mb-5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {book.heading || 'The Beauty of You'}
              </h2>
              <div className="reveal reveal-delay-1 divider-accent mb-6" />
              <p className="reveal reveal-delay-2 body-text mb-4">{book.description1}</p>
              <p className="reveal reveal-delay-2 body-text mb-8">{book.description2}</p>
              <div className="reveal reveal-delay-3 flex flex-wrap items-center gap-4">
                {bookLink ? (
                  <a href={bookLink} target="_blank" rel="noopener noreferrer" className="btn-primary arrow-hover" data-testid="homepage-book-buy-cta">
                    Buy Now — ${book.price || '25.95'} <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button onClick={() => openForm('personal_dev')} className="btn-primary arrow-hover" data-testid="homepage-book-buy-cta">
                    Inquire About the Book <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <Link to="/personal" className="btn-ghost arrow-hover" data-testid="homepage-book-learn-more">
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Focus */}
      <section className="section-padding bg-white" data-testid="industry-section">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <div className="grid grid-cols-12 gap-3">
                <div className="reveal-image col-span-7 aspect-[3/4] image-frame"><img src="/images/SalonTeam/Website/BUSINESS_DIS/Salons/SALON_team_working.JPG" alt="Salon team working" className="w-full h-full object-cover" /></div>
                <div className="col-span-5 flex flex-col gap-3">
                  <div className="reveal-image reveal-delay-2 aspect-square image-frame"><img src="/images/SalonTeam/Website/PERSONAL_BOY/Book/IMG_2401.JPG" alt="Beauty consultation" className="w-full h-full object-cover" /></div>
                  <div className="reveal-image reveal-delay-3 flex-1 image-frame"><img src="/images/SalonTeam/Website/BUSINESS_DIS/Salons/Salon exterior.jpg" alt="Salon exterior" className="w-full h-full object-cover" /></div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-6 lg:pl-8">
              <span className="reveal overline-text mb-4 block">Built for Beauty</span>
              <h2 className="reveal reveal-delay-1 heading-lg mb-6">Coaching Designed for the Beauty Industry</h2>
              <div className="reveal reveal-delay-1 divider-gradient w-24 mb-6" />
              <p className="reveal reveal-delay-2 body-text mb-6">With years of experience working with salon owners and beauty professionals, Natasha understands the unique challenges of growing a beauty business.</p>
              <p className="reveal reveal-delay-2 body-text mb-8">From team management to client retention, from pricing strategies to scaling operations — these programs are built for your industry.</p>
              <div className="reveal reveal-delay-3 flex flex-wrap gap-3 mb-8">
                <span className="badge">Salon Growth</span><span className="badge-turquoise">Team Building</span><span className="badge">Client Retention</span>
              </div>
              <Link to="/business" className="reveal reveal-delay-3 btn-primary arrow-hover" data-testid="industry-cta">Explore Business Programs <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Workshops Gallery */}
      <section className="section-padding bg-cream" data-testid="events-gallery">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="reveal overline-text mb-4 block">In Action</span>
              <h2 className="reveal reveal-delay-1 heading-lg">Workshops & Training</h2>
            </div>
            <button onClick={() => openForm('workshop')} className="reveal btn-outline arrow-hover self-start" data-testid="events-cta">
              Book a Workshop <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="reveal-image md:col-span-2 md:row-span-2 image-frame img-zoom rounded-2xl"><img src="/images/Events/IMG_7592.JPG" alt="Workshop session" className="w-full h-full object-cover" /></div>
            <div className="reveal-image reveal-delay-1 image-frame img-zoom rounded-2xl aspect-square"><img src="/images/Events/IMG_6389.JPG" alt="Training" className="w-full h-full object-cover" /></div>
            <div className="reveal-image reveal-delay-2 image-frame img-zoom rounded-2xl aspect-square"><img src="/images/Events/IMG_8225.JPG" alt="Team training" className="w-full h-full object-cover" /></div>
            <div className="reveal-image reveal-delay-3 image-frame img-zoom rounded-2xl aspect-square"><img src="/images/Events/IMG_7721.JPG" alt="Event" className="w-full h-full object-cover" /></div>
            <div className="reveal-image reveal-delay-4 image-frame img-zoom rounded-2xl aspect-square"><img src="/images/Events2/EVENT 2/224A2232.jpg" alt="Speaking" className="w-full h-full object-cover object-[center_30%]" /></div>
          </div>
        </div>
      </section>

      {/* CTA — vibrant pink-to-purple gradient */}
      <section className="section-padding relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E84993 0%, #B565CC 50%, #9B4FBF 100%)' }} data-testid="final-cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="container-padding relative">
          <div className="max-w-2xl mx-auto text-center">
            <span className="reveal inline-flex items-center gap-2 text-white/90 text-sm font-medium mb-6"><Star className="w-4 h-4" />Ready to Transform?</span>
            <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-medium text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Let's Build Your Beauty Business Together</h2>
            <p className="reveal reveal-delay-2 text-white/80 mb-10 max-w-lg mx-auto">Take the first step towards scaling your salon and discovering your full potential.</p>
            <button onClick={() => openForm('booking_call')} className="reveal reveal-delay-3 inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#E84993] text-xs font-medium tracking-[0.15em] uppercase rounded-full hover:-translate-y-0.5 transition-all hover:shadow-lg arrow-hover" data-testid="final-book-call">
              Book a Free Call <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
